import { Controller } from '@hotwired/stimulus';
import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.css';
import { jpegFileName } from '../utils/photoCrop';

// Matches ExportListItemsController::ID_CARD_CROP_ASPECT_RATIO - the view
// passes that same number in via data-photo-crop-aspect-ratio-value, so this
// is only the fallback if that attribute is ever missing.
const DEFAULT_ASPECT_RATIO = 0.8;

// An ID card photo doesn't need more than this many pixels on the long
// edge - capping avoids uploading a multi-megapixel crop from a modern phone
// photo. Only ever downscales; a smaller source is left at its own size
// (getCroppedCanvas would otherwise upscale a small crop to fill this,
// blurring it for no benefit).
const MAX_LONG_EDGE = 1600;

// "Student Image" upload on the admin Edit Student / New Student forms
// (export_list_items#create / #update). Some schools' webcams only shoot
// landscape, but this photo feeds ID cards, which want an 8x10-ish portrait
// crop. Rather than silently center-cropping on the server (the old
// behavior - see ExportListItemsController#crop_landscape_non_purchasable_image!,
// which still runs as a fallback if this never fires), show the picked photo
// in an interactive crop box the user can drag/resize, then keep the file
// input's file in sync with the current crop as they adjust it.
//
// The crop is applied eagerly (on the cropper's `ready` and `cropend`
// events), not deferred to form submit: submitting is then just the normal
// rails-ujs multipart upload, unmodified, of whatever file the input already
// holds. An earlier version intercepted the form's `submit` event instead,
// relying on a form-level Stimulus listener running before rails-ujs's own
// document-level delegated one - a real but implicit ordering, undocumented
// anywhere except this comment, that a future rails-ujs change or another
// submit listener could silently break. Applying the crop as the user
// interacts removes that dependency entirely.
//
// Rails performs no image processing (CLAUDE.md) - the crop happens entirely
// client-side via <canvas>; the server just receives an already-cropped file
// like any other upload.
export default class extends Controller {
  static targets = ['input', 'editor', 'image', 'error'];
  static values = { aspectRatio: { type: Number, default: DEFAULT_ASPECT_RATIO } };

  connect() {
    this.cropper = null;
    this.previewUrl = null;
    // Bumped on every applyCrop() call so a slow/delayed toBlob callback from
    // an earlier crop can tell it's stale (the user has since adjusted the
    // box again, or picked a new file) and skip overwriting a newer result.
    this.cropGeneration = 0;
  }

  disconnect() {
    this.destroyCropper();
    this.revokePreviewUrl();
  }

  selectFile() {
    const file = this.inputTarget.files[0];

    this.destroyCropper();
    this.revokePreviewUrl();
    if (this.hasErrorTarget) this.errorTarget.hidden = true;

    if (!file) {
      this.editorTarget.hidden = true;
      return;
    }

    this.previewUrl = URL.createObjectURL(file);
    this.imageTarget.onerror = () => this.showLoadError();
    this.imageTarget.onload = () => {
      this.cropper = new Cropper(this.imageTarget, {
        aspectRatio: this.aspectRatioValue,
        viewMode: 1,
        autoCropArea: 1,
        background: false,
        responsive: true,
        ready: () => this.applyCrop(),
        cropend: () => this.applyCrop(),
      });
    };
    this.imageTarget.src = this.previewUrl;
    this.editorTarget.hidden = false;
  }

  resetCrop() {
    if (!this.cropper) return;

    this.cropper.reset();
    this.applyCrop();
  }

  applyCrop() {
    const cropOptions = { imageSmoothingQuality: 'high' };
    // aspectRatio is width/height and is always < 1 (a portrait ID card
    // crop), so height - not width - is the long edge to cap.
    const cropData = this.cropper.getData();
    if (cropData.height > MAX_LONG_EDGE) {
      cropOptions.height = MAX_LONG_EDGE;
      cropOptions.width = Math.round(MAX_LONG_EDGE * this.aspectRatioValue);
    }

    const canvas = this.cropper.getCroppedCanvas(cropOptions);
    if (!canvas) return;

    const fileName = jpegFileName(this.inputTarget.files[0]?.name);
    this.cropGeneration += 1;
    const generation = this.cropGeneration;

    canvas.toBlob((blob) => {
      if (!blob || generation !== this.cropGeneration) return;

      const file = new File([blob], fileName, { type: 'image/jpeg' });
      const transfer = new DataTransfer();
      transfer.items.add(file);
      this.inputTarget.files = transfer.files;
    }, 'image/jpeg', 0.92);
  }

  showLoadError() {
    this.destroyCropper();
    this.editorTarget.hidden = true;
    if (this.hasErrorTarget) this.errorTarget.hidden = false;
  }

  destroyCropper() {
    if (this.cropper) {
      this.cropper.destroy();
      this.cropper = null;
    }
  }

  revokePreviewUrl() {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    }
  }
}
